import React from 'react';
import Chip from './Chip';
import Icon from './Icon';
import type { DaySummary } from '../lib/attendanceWeek';

function statusChipVariant(status: DaySummary['status']): 'success' | 'danger' | 'warn' | 'neutral' {
  if (status === 'present') return 'success';
  if (status === 'absent') return 'danger';
  if (status === 'weekend' || status === 'holiday') return 'warn';
  return 'neutral';
}

// ── Weekly table (per-day row: shift bar · status/range · hours) ───────────
export function AttendanceWeekTable({
  days,
  shiftLabel,
}: {
  days: DaySummary[];
  shiftLabel: string;
}) {
  return (
    <div className="t-card">
      <div className="card-head"><h3>This Week</h3></div>
      <table className="t-table">
        <tbody>
          {days.map((day) => (
            <tr key={day.dateKey} style={day.isToday ? { background: 'var(--accent-soft, #eff6ff)' } : undefined}>
              <td style={{ width: 90 }}>
                <div style={{ color: 'var(--ink-3)', fontSize: 'var(--fs-sm)' }}>{day.dayShort}</div>
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: 15,
                    ...(day.isToday
                      ? {
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: 26,
                          height: 26,
                          borderRadius: 8,
                          background: 'var(--accent, #3b82f6)',
                          color: 'white',
                        }
                      : {}),
                  }}
                >
                  {String(day.dayNum).padStart(2, '0')}
                </div>
              </td>
              <td>
                <div
                  style={{
                    display: 'inline-flex',
                    flexDirection: 'column',
                    padding: '6px 12px',
                    borderLeft: '3px solid var(--ink-4, #d1d5db)',
                    background: 'var(--surface-2, #f9fafb)',
                    borderRadius: 4,
                  }}
                >
                  <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 600 }}>{shiftLabel}</span>
                </div>
              </td>
              <td>
                {day.status === 'weekend' || day.status === 'holiday' ? (
                  <>
                    <div>{day.rangeLabel}</div>
                    <Chip variant={statusChipVariant(day.status)}>{day.statusLabel}</Chip>
                  </>
                ) : day.status === 'future' || day.status === 'none' ? null : (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span>{day.rangeLabel}</span>
                      {day.hoursLabel && (
                        <span style={{ color: 'var(--ink-3)' }}>· {day.hoursLabel}</span>
                      )}
                    </div>
                    {day.statusLabel && <Chip variant={statusChipVariant(day.status)}>{day.statusLabel}</Chip>}
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Compact weekly timeline widget ──────────────────────────────────────────
export function AttendanceWeekTimeline({
  days,
  shiftLabel,
  rangeLabel,
}: {
  days: DaySummary[];
  shiftLabel: string;
  rangeLabel: string;
}) {
  return (
    <div className="t-card" style={{ marginBottom: 20 }}>
      <div className="card-body">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background: 'var(--accent-soft, #eff6ff)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Icon name="clock" size={18} color="var(--accent, #3b82f6)" />
          </div>
          <div>
            <div style={{ fontWeight: 700 }}>Work Schedule</div>
            <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)' }}>{rangeLabel}</div>
          </div>
        </div>

        <div
          style={{
            display: 'inline-block',
            padding: '6px 12px',
            borderLeft: '3px solid var(--ink-4, #d1d5db)',
            background: 'var(--surface-2, #f9fafb)',
            borderRadius: 4,
            marginBottom: 20,
          }}
        >
          <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 600 }}>{shiftLabel}</span>
        </div>

        <div style={{ display: 'flex', position: 'relative' }}>
          {days.map((day) => (
            <div key={day.dateKey} style={{ flex: 1, textAlign: 'center', position: 'relative' }}>
              <div
                style={{
                  width: day.isToday ? 10 : 7,
                  height: day.isToday ? 10 : 7,
                  borderRadius: '50%',
                  background: day.isToday ? 'var(--accent, #3b82f6)' : 'var(--ink-4, #d1d5db)',
                  margin: '0 auto 10px',
                  position: 'relative',
                  zIndex: 1,
                }}
              />
              <div style={{ position: 'absolute', top: 3, left: 0, right: 0, height: 1, background: 'var(--ink-4, #e5e7eb)', zIndex: 0 }} />
              <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)' }}>{day.dayShort}</div>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: 15,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: 24,
                  height: 24,
                  borderRadius: 6,
                  background: day.isToday ? 'var(--accent, #3b82f6)' : 'transparent',
                  color: day.isToday ? 'white' : 'var(--ink)',
                }}
              >
                {String(day.dayNum).padStart(2, '0')}
              </div>
              <div
                style={{
                  marginTop: 4,
                  fontSize: 'var(--fs-xs)',
                  fontWeight: 600,
                  color:
                    day.status === 'present'
                      ? 'var(--success, #16a34a)'
                      : day.status === 'absent'
                        ? 'var(--danger, #dc2626)'
                        : day.status === 'weekend' || day.status === 'holiday'
                          ? 'var(--warn, #d97706)'
                          : 'var(--ink-3)',
                }}
              >
                {day.status === 'weekend' || day.status === 'holiday' ? day.statusLabel : day.statusLabel || ' '}
              </div>
              {day.hoursLabel && (
                <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)' }}>{day.hoursLabel}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
